<!--
// app/components/pages/region-matches/MatchTurns.vue
-->
<script setup lang="ts">
import type { Match, MatchCenterData } from "~/src/match/domain/models/Match";
import type { Team } from "~/src/team/domain/models/Team";
import BaseBackdropLoading from "~/app/components/base/BaseBackdropLoading.vue";
import TableRowMatchMatches from "~/app/components/pages/region-matches/TableRowMatchMatches.vue";
import KokTableRowMatchMatches from "~/app/components/pages/region-matches/KokTableRowMatchMatches.vue";
import HeaderTableMatches from "~/app/components/pages/region-matches/HeaderTableMatches.vue";
import { isFutureDate } from "~/src/Shared/utils";

type Props = {
  groupTurns: MatchCenterData[];
  teams: Record<number, Team>;
  loading: boolean;
  timeZone?: string;
  regionParamProp?: string;
  kokQualifiers?: boolean;
  seasonId?: number;
};

const props = withDefaults(defineProps<Props>(), {
  loading: false,
});

const { locale } = useI18n();
const localePath = useLocalePath();

const showGroup = (matches: Match[]): boolean => {
  const groupArr = matches.map(m => m.groupId);
  return [...new Set(groupArr)].length > 1;
};

const showTickets = (matches: Match[]): boolean => {
  const ticketsExistInSomeMatch = !!matches.find(m => m.metaInformation && m.metaInformation.tickets_url);
  const lastMatchDateIsFuture = !!isFutureDate(matches[matches.length - 1].date);
  return ticketsExistInSomeMatch && lastMatchDateIsFuture;
};
</script>

<template>
  <div class="kql-container-turns">
    <base-backdrop-loading :active="loading"></base-backdrop-loading>

    <div
      v-for="(turn, index) in groupTurns"
      :key="index"
      class="bg-gray-900 lg:px-6 px-4 py-4 rounded-lg mb-2"
    >
      <div class="flex gap-4 items-center">
        <h2 class="px-2 py-0.5 rounded border-primary border w-fit text-primary mb-6 lg:text-sm text-[10px]">
          {{ turn.turnName }}
        </h2>
      </div>

      <header-table-matches
        v-if="turn.turnName !== 'Finalissima'"
        :show-group="showGroup(turn.matches)"
        :show-tickets="showTickets(turn.matches)"
      ></header-table-matches>

      <div v-if="kokQualifiers" class="kql-container-match">
        <kok-table-row-match-matches
          v-for="match in turn.matches"
          :key="match.id"
          :match="match"
          :away-team="teams[match.participants.awayTeamId]"
          :home-team="teams[match.participants.homeTeamId]"
          :show-group="showGroup(turn.matches)"
          :time-zone="timeZone"
          :region-param="regionParamProp"
          :season-id="seasonId"
        ></kok-table-row-match-matches>
      </div>
      <div v-else-if="turn.turnName === 'Finalissima'" class="kql-container-match">
        <nuxt-link
          :to="localePath({ name: 'FinalissimaQueens' })"
          external
          class="h-full flex rounded-lg bg-[url('/img/landings/bg-finalissima-top.jpg')] bg-right-top bg-no-repeat bg-cover text-center"
        >
          <nuxt-img
            src="/img/league-logos/queens_finalissima_h.png"
            alt="Queens Finalissima Paris 2025"
            width="568"
            height="720"
            quality="90"
            fit="outside"
            class="w-32 py-2 mx-auto"
          ></nuxt-img>
        </nuxt-link>
      </div>
      <div v-else class="kql-container-match">
        <table-row-match-matches
          v-for="match in turn.matches"
          :key="match.id"
          :match="match"
          :away-team="teams[match.participants.awayTeamId]"
          :home-team="teams[match.participants.homeTeamId]"
          :show-group="showGroup(turn.matches)"
          :show-tickets="showTickets(turn.matches)"
        ></table-row-match-matches>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kql-container-turns {
  @apply relative;
}
</style>
